import { useAsync, useMountEffect } from "@react-hookz/web";
import set from "date-fns/set";
import { useAtomValue } from "jotai";
import { useEffect } from "react";

import { useFallbackStorageValue } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { addDays, isAfterOrEqual } from "shared/lib/utils";

import {
  MyDailyFiveArticleIdsStorageKey,
  MyDailyFiveLastUpdateTimeStorageKey,
  MyDailyFiveRecommendIdStorageKey,
} from "scmp-app/components/home/my-news-daily-five/consts";
import { fetchAiEngineData } from "scmp-app/lib/ai-engine/apis";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";

let isFetching = false;
let hasFetched = false;

type UseMyNewDailyFiveArticleIdsParameters = {
  authorIds?: string[];
  sectionIds?: string[];
  topicIds?: string[];
};

export const useMyNewDailyFiveArticleIds = ({
  authorIds,
  sectionIds,
  topicIds,
}: UseMyNewDailyFiveArticleIdsParameters) => {
  const { set: setExpiredDateTime, value: expiredDateTime } = useFallbackStorageValue(
    MyDailyFiveLastUpdateTimeStorageKey,
    {
      defaultValue: "0",
    },
  );

  const { set: setLocalStorageRecommendedArticleIds, value: localStorageRecommendedArticleIds } =
    useFallbackStorageValue<string[]>(MyDailyFiveArticleIdsStorageKey, {
      defaultValue: [],
    });

  const { set: setMyDailyFiveRecommendIdStorageKey, value: localStorageRecommendedId } =
    useFallbackStorageValue(MyDailyFiveRecommendIdStorageKey, {
      defaultValue: "",
    });

  const { user } = useAtomValue(accountAtom);

  const [{ result }, { execute }] = useAsync(() =>
    fetchAiEngineData<AiEngineDataType.MyNewsFresh>({
      authorIds,
      sectionEntityIds: sectionIds,
      topicIds,
      type: AiEngineDataType.MyNewsFresh,
      userUuid: user?.uuid,
    }),
  );

  const isExpired = expiredDateTime ? isAfterOrEqual(new Date(), expiredDateTime) : true;

  useMountEffect(() => {
    const followCount =
      (authorIds?.length ?? 0) + (sectionIds?.length ?? 0) + (topicIds?.length ?? 0);

    if (!isExpired || followCount < 3 || isFetching || hasFetched) {
      return;
    }

    isFetching = true;
    hasFetched = true;
    void execute();
  });

  useEffect(() => {
    const data = result?.data;
    if (!data) {
      isFetching = false;
      return;
    }

    setMyDailyFiveRecommendIdStorageKey(data.rec_id ?? "");

    const recommendedArticleIds = data.results.map(result => result.id) ?? [];
    setLocalStorageRecommendedArticleIds(recommendedArticleIds);

    const expiredDateTime = addDays(set(new Date(), { hours: 4, minutes: 59 }), 1).toISOString();
    setExpiredDateTime(expiredDateTime);

    isFetching = false;
  }, [
    result?.data,
    setExpiredDateTime,
    setLocalStorageRecommendedArticleIds,
    setMyDailyFiveRecommendIdStorageKey,
  ]);

  return {
    recommendedArticleIds: localStorageRecommendedArticleIds,
    recommendedId: localStorageRecommendedId,
  };
};
